import { Group, Loader, ScrollArea } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { AudioWaveform, Music, Speech, Upload } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { Asset, AssetOrder } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useMembership } from "../../platform/membership";
import { useAssetDelete } from "../../platform/media/asset-delete";
import { CreatorFilter } from "../../platform/media/CreatorFilter";
import { LibraryPreview } from "../../platform/media/LibraryPreview";
import {
  LIBRARY_ORDERS,
  LIBRARY_SEARCH_MAX,
  useLibraryPages,
  type LibraryFilter,
} from "../../platform/media/library-pages";
import { canEditContent } from "../../platform/permissions";
import { readPref, writePref } from "../../platform/prefs";
import { ColumnsSetting } from "../../ui/ColumnsSetting";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { SortMenu } from "../../ui/SortMenu";
import { SoundWave } from "../../ui/SoundWave";
import { SubmitSearch } from "../../ui/SubmitSearch";
import { TypeTabs } from "../../ui/TypeTabs";
import { MiniPlayer } from "./MiniPlayer";
import { SoundTile } from "./SoundTile";
import classes from "./sounds.module.css";

/** Live's column counts for the sounds grid (capture 06). */
const COLUMN_MARKS = [2, 4, 6, 8, 10] as const;
/** Live's page of the Sounds library (`ASSETS_DEFAULT_LIMIT`). */
const SOUNDS_PAGE = 24;

/** Live's chips over the sounds (`SOUND_TYPE_FILTER`): by how a file was made. */
export type SoundType = "all" | "sfx" | "tts" | "music" | "uploads";

/** The page's search, chip and order, kept across its tabs as live's `sounds-filters-context`. */
export interface SoundsFilter {
  search: string;
  type: SoundType;
  createdBy: string[];
  order: AssetOrder;
}

export const NO_SOUNDS_FILTER: SoundsFilter = {
  search: "",
  type: "all",
  createdBy: [],
  order: "newest",
};

/** A chip as the list route's filter (#308): a job type, or the uploads. */
const BY_TYPE: Record<SoundType, Pick<LibraryFilter, "generationType" | "source">> = {
  all: {},
  sfx: { generationType: "audio.sfx" },
  tts: { generationType: "audio.tts" },
  music: { generationType: "audio.music" },
  uploads: { source: ["upload"] },
};

export interface SoundsLibraryProps {
  teamId: string;
  brandId: string;
  filter: SoundsFilter;
  onFilter: (change: Partial<SoundsFilter>) => void;
}

/**
 * The brand's audio files (S1's list, `kind=audio`) as live's square tiles in a grid of live's
 * column count, under live's search, creator chips, sound-type chips and sort (#308's and #388's
 * filters). A tile plays its file in the mini player at the top and offers a download on hover;
 * its ⤢ opens the library's lightbox over the list (SA1 decision 8), with the library's actions
 * where the member may use them, as the Studio's Library tab (G1 decision 29).
 */
export function SoundsLibrary({ teamId, brandId, filter, onFilter }: SoundsLibraryProps) {
  const sounds = useLibraryPages(teamId, brandId, {
    kind: "audio",
    trash: false,
    search: filter.search,
    createdBy: filter.createdBy,
    order: filter.order,
    ...BY_TYPE[filter.type],
  }, true, { size: SOUNDS_PAGE });
  const [columns, setColumns] = useState(() => readPref("soundColumns"));
  const [playing, setPlaying] = useState<Asset | null>(null);
  // The file the lightbox shows, by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const items = sounds.data?.pages.flatMap((page) => page.items) ?? [];
  const more = morePages(sounds);
  const readAgain = () => void sounds.refetch();
  const me = useMembership(teamId);
  const mayChange = (asset: Asset) =>
    me !== undefined && canEditContent(me.role, asset.created_by ?? "", me.userId);
  const deletion = useAssetDelete(teamId, brandId);

  return (
    <div className={classes.library}>
      <Toolbar
        teamId={teamId}
        filter={filter}
        onFilter={onFilter}
        columns={columns}
        onColumns={(next) => {
          setColumns(next);
          writePref("soundColumns", next);
        }}
      />
      {/* The list scrolls on its own under the toolbar, which stays (live's `Library`, issue 784). */}
      <ScrollArea type="auto" offsetScrollbars className={classes.list}>
        {sounds.isPending ? <Loader size="sm" aria-label="Loading the sounds" /> : null}
        {sounds.isError ? <ErrorAlert error={sounds.error} /> : null}
        {sounds.isSuccess && items.length === 0 ? (
          <EmptyState
            image={<SoundWave className={classes.emptyWave} />}
            title="No sounds yet"
            description="Generated and uploaded audio will appear here."
          />
        ) : null}
        {items.length > 0 ? (
          <ul
            className={classes.grid}
            style={{ ["--sound-columns" as string]: columns }}
            aria-label="Sounds"
          >
            {items.map((asset) => (
              <li key={asset.id}>
                <SoundTile
                  asset={asset}
                  playing={playing?.id === asset.id}
                  onPlay={() => setPlaying(asset)}
                  onPreview={() => {
                    // The lightbox plays the sound itself: the mini player gives way.
                    setPlaying(null);
                    setPreviewing(asset.id);
                  }}
                />
              </li>
            ))}
          </ul>
        ) : null}
        {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
        <LoadAhead more={more} loaded={items.length} label="Loading more sounds" />
      </ScrollArea>
      {playing === null || playing.urls.original === null ? null : (
        <MiniPlayer
          key={playing.id}
          url={playing.urls.original}
          name={playing.name}
          durationSeconds={playing.duration_ms === null ? null : playing.duration_ms / 1000}
          onClose={() => setPlaying(null)}
          onError={readAgain}
        />
      )}
      <LibraryPreview
        teamId={teamId}
        brandId={brandId}
        assets={items}
        current={previewing}
        onShow={setPreviewing}
        more={more}
        trash={false}
        mayChange={mayChange}
        onDelete={deletion.ask}
        onRestore={() => undefined}
        restoring={null}
        paused={deletion.asking}
      />
      {deletion.dialog}
    </div>
  );
}

/** Live's chips over the sounds, in its order, words and icons. */
const SOUND_TYPES: { value: SoundType; label: string; icon?: ReactNode }[] = [
  { value: "all", label: "All" },
  { value: "sfx", label: "Sound effects", icon: <AudioWaveform size={16} /> },
  { value: "tts", label: "Text-to-speech", icon: <Speech size={16} /> },
  { value: "music", label: "Music", icon: <Music size={16} /> },
  { value: "uploads", label: "Manual Uploads", icon: <Upload size={16} /> },
];

interface ToolbarProps {
  teamId: string;
  filter: SoundsFilter;
  onFilter: (change: Partial<SoundsFilter>) => void;
  columns: number;
  onColumns: (next: number) => void;
}

/** Live's row over the sounds: the search, the chips; on the right the sort and the columns. */
function Toolbar({ teamId, filter, onFilter, columns, onColumns }: ToolbarProps) {
  // On a phone the search, the creators and the types wrap onto lines of their own (the toolbar's
  // CSS, below 48em); wider, live's one line that scrolls sideways.
  const phone = useMediaQuery("(max-width: 48em)") === true;
  return (
    <div className={classes.toolbar}>
      <Group gap="sm" wrap={phone ? "wrap" : "nowrap"} className={classes.filters}>
        <SubmitSearch
          className={classes.search}
          label="Search by asset name or partial match"
          value={filter.search}
          maxLength={LIBRARY_SEARCH_MAX}
          onSearch={(search) => onFilter({ search })}
        />
        <CreatorFilter
          teamId={teamId}
          value={filter.createdBy}
          onChange={(createdBy) => onFilter({ createdBy })}
        />
        <TypeTabs
          label="Sound type"
          value={filter.type}
          onChange={(value) => {
            const type = SOUND_TYPES.find((each) => each.value === value);
            if (type !== undefined) onFilter({ type: type.value });
          }}
          tabs={SOUND_TYPES}
        />
      </Group>
      <Group gap={4} wrap="nowrap">
        <SortMenu
          options={LIBRARY_ORDERS}
          value={filter.order}
          onChange={(order) => onFilter({ order })}
        />
        <ColumnsSetting value={columns} onChange={onColumns} marks={COLUMN_MARKS} />
      </Group>
    </div>
  );
}
